// JOBE-0142.4: PonderedBody replaces the Slice 1 demo stub with a real
// fetch to POST /api/threads/{urn}/messages/{seq}/ponder. The server
// calls memory.Project and returns the projection result; this component
// renders it. If the server returns 501 (memory.Project not wired in
// this deployment), an informational callout is shown instead of an error.

import { useState, useEffect } from 'react';

export function PonderedBody({ threadURN, msgSeq }) {
  const [state, setState] = useState('loading'); // 'loading' | 'ok' | 'unavailable' | 'error'
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!threadURN || msgSeq == null) {
      setState('error');
      return;
    }
    setState('loading');
    setResult(null);

    let cancelled = false;
    fetch(
      `/api/threads/${encodeURIComponent(threadURN)}/messages/${encodeURIComponent(msgSeq)}/ponder`,
      { method: 'POST', credentials: 'same-origin' },
    )
      .then((r) => {
        if (cancelled) return;
        if (r.status === 501) { setState('unavailable'); return; }
        if (!r.ok) { setState('error'); return; }
        return r.json().then((data) => {
          if (cancelled) return;
          setResult(data);
          setState('ok');
        });
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [threadURN, msgSeq]);

  if (state === 'loading') {
    return (
      <div className="v2-pondered-body" data-testid="v2-pondered-body">
        <div className="v2-pondered-loading dim">Projecting memory context…</div>
      </div>
    );
  }

  if (state === 'unavailable') {
    return (
      <div className="v2-pondered-body" data-testid="v2-pondered-body">
        <div className="v2-pondered-unavailable dim">
          Memory projection is not available in this deployment.
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="v2-pondered-body" data-testid="v2-pondered-body">
        <div className="v2-pondered-error dim">Could not load memory context.</div>
      </div>
    );
  }

  // state === 'ok'
  return (
    <div className="v2-pondered-body" data-testid="v2-pondered-body">
      {result && result.sections ? (
        result.sections.map((section, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="v2-pondered-section">
            {section.title && (
              <div className="v2-pondered-section-title">{section.title}</div>
            )}
            {section.items && section.items.length > 0 ? (
              <ul className="v2-pondered-items">
                {section.items.map((item, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={j} className="v2-pondered-item">{item}</li>
                ))}
              </ul>
            ) : (
              <div className="v2-pondered-empty dim">Nothing to show.</div>
            )}
          </div>
        ))
      ) : (
        <div className="v2-pondered-section">
          <div className="v2-pondered-raw dim">
            {result ? JSON.stringify(result, null, 2) : 'No projection data.'}
          </div>
        </div>
      )}
    </div>
  );
}
